import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  X
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type MouseEvent
} from "react";
import type { WebWorkspaceSummaryDto } from "../../api/schemas";
import { useWorkspaceQuery } from "../../api/hooks";
import { restoreWorkspaceState } from "../../domain/workspace";

const RECENT_WORKSPACE_LIMIT = 10;
const PREVIEW_DELAY_MS = 450;

interface WorkspaceSidebarProps {
  collapsed: boolean;
  activeWorkspaceId: string | undefined;
  workspaces: WebWorkspaceSummaryDto[];
  loadError: string | undefined;
  onToggleCollapsed: () => void;
  onNewWorkspace: () => void;
  onSelectWorkspace: (id: string) => void;
}

interface WorkspaceSearchResult {
  workspace: WebWorkspaceSummaryDto;
  documentNumberMatched: boolean;
}

export function WorkspaceSidebar({
  collapsed,
  activeWorkspaceId,
  workspaces,
  loadError,
  onToggleCollapsed,
  onNewWorkspace,
  onSelectWorkspace
}: WorkspaceSidebarProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [previewWorkspaceId, setPreviewWorkspaceId] = useState<string>();
  const [previewLeft, setPreviewLeft] = useState(0);
  const [previewTop, setPreviewTop] = useState(0);
  const previewTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const normalizedQuery = normalizeSearchValue(searchQuery);
  const previewQuery = useWorkspaceQuery(previewWorkspaceId, Boolean(previewWorkspaceId));

  const searchResults = useMemo(
    () => findWorkspaceMatches(workspaces, normalizedQuery),
    [normalizedQuery, workspaces]
  );
  const visibleResults =
    normalizedQuery || showAll
      ? searchResults
      : searchResults.slice(0, RECENT_WORKSPACE_LIMIT);
  const hiddenWorkspaceCount = workspaces.length - visibleResults.length;
  const previewState = useMemo(
    () =>
      previewQuery.data
        ? restoreWorkspaceState(previewQuery.data.state).state
        : undefined,
    [previewQuery.data]
  );

  useEffect(
    () => () => {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
      }
    },
    []
  );

  function closePreview() {
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
    }
    previewTimerRef.current = undefined;
    setPreviewWorkspaceId(undefined);
  }

  function schedulePreview(
    workspaceId: string,
    anchor: HTMLElement,
    delay = PREVIEW_DELAY_MS
  ) {
    closePreview();
    const anchorRect = anchor.getBoundingClientRect();
    const left = Math.max(8, Math.min(anchorRect.right + 10, window.innerWidth - 328));
    const top = Math.max(8, Math.min(anchorRect.top, window.innerHeight - 240));
    previewTimerRef.current = setTimeout(() => {
      setPreviewLeft(left);
      setPreviewTop(top);
      setPreviewWorkspaceId(workspaceId);
    }, delay);
  }

  function handleWorkspaceMouseEnter(
    workspaceId: string,
    event: MouseEvent<HTMLButtonElement>
  ) {
    schedulePreview(workspaceId, event.currentTarget);
  }

  function handleWorkspaceFocus(
    workspaceId: string,
    event: FocusEvent<HTMLButtonElement>
  ) {
    schedulePreview(workspaceId, event.currentTarget, 0);
  }

  if (collapsed) {
    return (
      <aside className="workspace-sidebar workspace-sidebar-collapsed" aria-label="Workspaces">
        <button
          type="button"
          className="icon-button"
          aria-label="Expand workspaces"
          onClick={onToggleCollapsed}
        >
          <ChevronRight aria-hidden="true" size={18} />
        </button>
        <button
          type="button"
          className="icon-button"
          aria-label="New workspace"
          onClick={onNewWorkspace}
        >
          <Plus aria-hidden="true" size={18} />
        </button>
      </aside>
    );
  }

  return (
    <aside className="workspace-sidebar" aria-label="Workspaces">
      <div className="sidebar-heading">
        <h2>Workspaces</h2>
        <button
          type="button"
          className="icon-button"
          aria-label="Collapse workspaces"
          onClick={onToggleCollapsed}
        >
          <ChevronLeft aria-hidden="true" size={18} />
        </button>
      </div>

      <button type="button" className="secondary-button" onClick={onNewWorkspace}>
        <Plus aria-hidden="true" size={16} />
        New quote/proforma
      </button>

      <div className="workspace-search">
        <Search aria-hidden="true" size={15} />
        <input
          type="search"
          value={searchQuery}
          aria-label="Search workspaces by title or document number"
          placeholder="Search title or document no."
          onChange={(event) => setSearchQuery(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape" && searchQuery) {
              event.preventDefault();
              setSearchQuery("");
            }
          }}
        />
        {searchQuery && (
          <button
            type="button"
            className="workspace-search-clear"
            aria-label="Clear workspace search"
            onClick={() => setSearchQuery("")}
          >
            <X aria-hidden="true" size={14} />
          </button>
        )}
      </div>

      {loadError && <p className="error-text">{loadError}</p>}

      <div className="workspace-list-heading">
        <span>{normalizedQuery ? "Search Results" : "Previous Workspaces"}</span>
        <span className="workspace-list-count" aria-live="polite">
          {normalizedQuery ? `${searchResults.length} of ${workspaces.length}` : workspaces.length}
        </span>
      </div>

      <div className="workspace-list" onScroll={closePreview}>
        {visibleResults.map(({ workspace, documentNumberMatched }) => (
          <button
            type="button"
            key={workspace.id}
            className={
              workspace.id === activeWorkspaceId
                ? "workspace-list-item workspace-list-item-active"
                : "workspace-list-item"
            }
            aria-describedby={
              workspace.id === previewWorkspaceId
                ? `workspace-preview-${workspace.id}`
                : undefined
            }
            onMouseEnter={(event) => handleWorkspaceMouseEnter(workspace.id, event)}
            onMouseLeave={closePreview}
            onFocus={(event) => handleWorkspaceFocus(workspace.id, event)}
            onBlur={closePreview}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                closePreview();
              }
            }}
            onClick={() => onSelectWorkspace(workspace.id)}
          >
            <span>{workspace.title}</span>
            <small>
              {documentNumberMatched && workspace.last_pdf_number
                ? workspace.last_pdf_number
                : formatUpdatedAt(workspace.updated_at)}
            </small>
          </button>
        ))}

        {normalizedQuery && searchResults.length === 0 && (
          <p className="workspace-empty-state">No workspaces found.</p>
        )}

        {!normalizedQuery && workspaces.length > RECENT_WORKSPACE_LIMIT && (
          <button
            type="button"
            className="workspace-list-toggle"
            onClick={() => {
              closePreview();
              setShowAll((current) => !current);
            }}
          >
            {showAll
              ? `Show recent ${RECENT_WORKSPACE_LIMIT}`
              : `Show ${hiddenWorkspaceCount} more`}
          </button>
        )}
      </div>

      {previewWorkspaceId && (
        <WorkspacePreview
          id={`workspace-preview-${previewWorkspaceId}`}
          left={previewLeft}
          top={previewTop}
          isLoading={previewQuery.isLoading}
          hasError={previewQuery.isError}
          documentType={previewState?.documentType}
          documentNumber={previewQuery.data?.last_pdf_number ?? undefined}
          items={previewState?.cart ?? []}
        />
      )}
    </aside>
  );
}

interface WorkspacePreviewProps {
  id: string;
  left: number;
  top: number;
  isLoading: boolean;
  hasError: boolean;
  documentType: "quotation" | "proforma" | undefined;
  documentNumber: string | undefined;
  items: ReturnType<typeof restoreWorkspaceState>["state"]["cart"];
}

function WorkspacePreview({
  id,
  left,
  top,
  isLoading,
  hasError,
  documentType,
  documentNumber,
  items
}: WorkspacePreviewProps) {
  const visibleItems = items.slice(0, 3);
  const hiddenItemCount = items.length - visibleItems.length;

  return (
    <div id={id} role="tooltip" className="workspace-preview" style={{ left, top }}>
      {isLoading ? (
        <p>Loading preview…</p>
      ) : hasError ? (
        <p>Preview unavailable.</p>
      ) : (
        <>
          <div className="workspace-preview-heading">
            <strong>{documentType === "proforma" ? "Proforma" : "Quotation"}</strong>
            {documentNumber && <span>{documentNumber}</span>}
          </div>
          {visibleItems.length === 0 ? (
            <p>No quote items saved.</p>
          ) : (
            <>
              <div className="workspace-preview-label">Items</div>
              <ul>
                {visibleItems.map((item) => (
                  <li key={item.id}>
                    <span>{item.brand} · {item.description}</span>
                    {item.quantity !== undefined && <small>Qty {item.quantity}</small>}
                  </li>
                ))}
              </ul>
              {hiddenItemCount > 0 && (
                <div className="workspace-preview-more">+{hiddenItemCount} more</div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function findWorkspaceMatches(
  workspaces: WebWorkspaceSummaryDto[],
  normalizedQuery: string
): WorkspaceSearchResult[] {
  if (!normalizedQuery) {
    return workspaces.map((workspace) => ({
      workspace,
      documentNumberMatched: false
    }));
  }

  return workspaces.flatMap((workspace) => {
    const titleMatched = normalizeSearchValue(workspace.title).includes(normalizedQuery);
    const documentNumberMatched = normalizeSearchValue(
      workspace.last_pdf_number ?? ""
    ).includes(normalizedQuery);

    return titleMatched || documentNumberMatched
      ? [{ workspace, documentNumberMatched }]
      : [];
  });
}

function normalizeSearchValue(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLocaleLowerCase("en-IN");
}

function formatUpdatedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit"
  });
}
